/****************
 * Trader data hooks - fetches REAL data from backend API
 * Used by Profile page for per-trader analytics
 ****************/

import { track } from '@/lib/analytics';
import { withLastGood, pollWhileOffline } from '@/lib/lastGood';
import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { traderApi, ordersApi, favoritesApi, bannedTradersApi, subscriptionApi, TraderListItem, TraderPerformanceResponse, TraderTradeResponse, InferredOrderResponse } from '@/api/client';
import { buildWalletCurve, heldFrom, type WalletCurve } from '@/lib/walletCurve';
import { withoutSimTraders } from '@/lib/simTraders';
import { usesOwnVenueMark } from '@/lib/venueScope';
import { useFetchAllPages } from './useFetchAllPages';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Fetches the order lifecycle for a trade.
 *
 * If subscriptionId is provided, hits the session endpoint that reads
 * replicated_orders (the user's own orders — covers OPEN/INCREASE/DECREASE/CLOSE
 * with real execution fees). Otherwise falls back to the trader endpoint that
 * reads inferred_orders (the master trader's inferred lifecycle).
 */
export const useTradeOrders = (tradeId: string | number | null, subscriptionId?: string | number | null) => {
  return useQuery({
    queryKey: ['trade-orders', tradeId, subscriptionId || 'master'],
    queryFn: async (): Promise<InferredOrderResponse[]> => {
      if (!tradeId) return [];
      if (subscriptionId) {
        const result = await subscriptionApi.getSessionTradeOrders(subscriptionId, tradeId);
        return result.orders || [];
      }
      const result = await traderApi.getTradeOrders(tradeId);
      return result.orders || [];
    },
    enabled: !!tradeId,
    staleTime: 30000,
    retry: 2,
  });
};

export const useTraders = (period: string = 'all', venue: string = 'binance', includeHidden = false) => {
  /*
   * SIM traders are ours, not the public's, and this list is where they did the
   * most damage.
   *
   * `withoutSimTraders` was written for exactly this, wired into the dashboard
   * leaderboard and the websocket feed, and never wired in here -- so the four
   * seeded test portfolios (SIM: 1kBalance, SIM: 10kBalance, SIM: VAIDIK, SIM:
   * Sim2Test) were still served to the browse list AND to the copy-trade
   * picker. The leaderboard was filtered while the page where somebody actually
   * chooses who to copy was not, which is the wrong way round: a dummy on a
   * leaderboard is embarrassing, a dummy in the picker is a session against
   * nothing.
   *
   * They show zero trades and zero PnL, so they also sort above every trader
   * with a real loss.
   *
   * Still UI-only, as the helper's own note says: the backend serves them to any
   * direct caller and a deep link to /traders/SIM_... resolves. That is the
   * durable fix and it is not this one.
   */
  const { isAdmin } = useAuth();
  return useQuery({
    // venue in the key so switching venue refetches. Defaults to 'binance' —
    // only the admin-only venue selector changes it. 2026-08-30.
    //
    // isAdmin is in the key too: an admin and a user get different lists, and
    // sharing one cache entry would show whichever arrived first.
    queryKey: ['traders', period, venue, isAdmin, includeHidden],
    queryFn: async (): Promise<TraderListItem[]> => {
      // Last good list during a backend restart (lib/lastGood.ts).
      return withLastGood<TraderListItem[]>(`traders:${period}:${venue}:${isAdmin}:${includeHidden}`, async () => {
        const rows = await traderApi.getAllTraders(undefined, period, venue, includeHidden);
        return withoutSimTraders(rows, isAdmin, (t) => t.id);
      });
    },
    structuralSharing: false,
    refetchInterval: (q) => pollWhileOffline<TraderListItem[]>(q),
    staleTime: 30000,
    retry: 3,
  });
};

export const useTraderWalletHistory = (traderId: string | null) => {
  return useQuery({
    queryKey: ['trader-wallet-history', traderId],
    queryFn: async () => {
      if (!traderId) return null;
      return traderApi.getWalletHistory(traderId);
    },
    enabled: !!traderId,
    staleTime: 60000,
  });
};

// Wallet curve built client-side by the shared, dependency-free module
// (src/lib/walletCurve.ts — copied verbatim from the mobile app so both
// clients draw the same curve from the same arithmetic). Transfers come from
// OUR backend's synced trader_transfers table, not Binance-direct — the
// module's own header notes this is the intended wiring once the backend job
// exists (it does: RunTransferSync, 6h cadence). 2026-08-29 (audit: web
// wallet chart didn't use the module / no transfer markers).
export const useTraderWalletCurve = (
  traderId: string | null,
  currentEquity: number | null,
  closedTrades: any[] | undefined,
  /** Open positions: with the closed ones, the margin in the market the curve never goes under (10-31). */
  openTrades?: any[] | undefined,
) => {
  const held = heldFrom(closedTrades, openTrades);
  const trades = (closedTrades ?? [])
    .filter((t: any) => t.exit_time)
    .map((t: any) => ({ exitAt: new Date(t.exit_time).getTime(), pnl: t.pnl_usd || 0 }))
    .filter((t: any) => Number.isFinite(t.exitAt));

  // The FIRST ORDER, not the first exit — the honest left edge of the x-axis.
  const firstOrderAt = (closedTrades ?? [])
    .map((t: any) => (t.entry_time ? new Date(t.entry_time).getTime() : NaN))
    .filter((n: number) => Number.isFinite(n))
    .reduce((min: number, n: number) => Math.min(min, n), Number.POSITIVE_INFINITY);

  return useQuery({
    // Trade count in the key: the curve rebuilds when a trade closes, without
    // refetching transfers (server-synced every 6h; 1h staleTime here).
    queryKey: ['wallet-curve', traderId ?? '', trades.length, held.length, currentEquity ?? 0],
    enabled: !!traderId,
    staleTime: 60 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const rows = await traderApi
        .getTraderTransfers(traderId!)
        .catch(() => ({ transfers: [] as any[] }));
      const transfers = (rows.transfers || []).map((r: any) => ({
        t: new Date(r.transferred_at).getTime(),
        amount: Number(r.amount) || 0, // already signed: positive is money in
        coin: String(r.coin ?? 'USDT'),
        kind: r.direction === 'WITHDRAWAL' ? ('WITHDRAWAL' as const) : ('DEPOSIT' as const),
      }));
      return buildWalletCurve({
        currentEquity,
        closedTrades: trades,
        transfers,
        firstOrderAt: Number.isFinite(firstOrderAt) ? firstOrderAt : undefined,
        positions: held,
      });
    },
  });
};

/****************
 * A TRADER'S EQUITY LINE, ONE SOURCE FOR EVERY PAGE (Guillaume, 10-31: "when
 * we copy him, the line drawn for his Equity in our session [must] match the
 * values of the line drawn on his profile").
 *
 * The profile and the session both read this, so the two lines are the same
 * points by construction: ALL his closed trades and his open positions (never
 * the profile's period filter: the wallet three months ago does not depend on
 * which period is being looked at), his wallet now, and the one exception --
 * a venue that records the account's own value (Hyperliquid) is drawn from
 * that record, the rebuilt line still listing the transfers.
 ****************/
export interface TraderEquity {
  /** What to draw: the venue's record or the rebuilt curve, {x, y} by time. */
  points: { x: number; y: number }[];
  /** The rebuilt curve (transfers, exact flag), also when the venue draws the line. */
  rebuilt: WalletCurve | null;
  /** True when the line is the venue's own record. */
  venue: boolean;
}

export const useTraderEquity = (traderId: string | null): TraderEquity => {
  const { data: perf } = useTraderPerformance(traderId, 'all');
  const { data: closed } = useTraderTrades(traderId, 'CLOSED', 'all');
  const { data: open } = useTraderTrades(traderId, 'OPEN', 'all');
  const { data: history } = useTraderWalletHistory(traderId);
  const equity = Number(perf?.margin_balance) > 0 ? Number(perf?.margin_balance) : null;
  const { data: curve } = useTraderWalletCurve(traderId, equity, closed, open);
  return useMemo(() => {
    const rebuilt = curve?.points && curve.points.length > 1 ? curve : null;
    const venue = usesOwnVenueMark(traderId) && ((history as any)?.points?.length ?? 0) > 1;
    if (!venue) return { points: rebuilt?.points ?? [], rebuilt, venue: false };
    const from = (history as any)?.reliable_from ? new Date((history as any).reliable_from).getTime() : null;
    const points = ((history as any).points as { t: string; balance: number }[])
      .map((p) => ({ x: new Date(p.t).getTime(), y: p.balance }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && (from === null || p.x >= from))
      .sort((a, b) => a.x - b.x);
    return { points, rebuilt, venue: true };
  }, [curve, history, traderId]);
};

export const useTraderPerformance = (traderId: string | null, period: string = 'all') => {
  return useQuery({
    queryKey: ['trader-performance', traderId, period],
    queryFn: async (): Promise<TraderPerformanceResponse | null> => {
      if (!traderId) return null;
      return traderApi.getTraderPerformance(traderId, period);
    },
    enabled: !!traderId,
    staleTime: 10000,
    retry: 3,
  });
};

/*
 * Paged, but `data` is still a flat array.
 *
 * This asked for 5,000 trades in one call. 5,000 was not a page size, it was a
 * CEILING -- a trader with more closed trades than that could not be read to
 * the end by any request, and the profile showed 5,000 while looking complete.
 * The backend now returns X-Total-Count (the count BEFORE the limit) and
 * X-Next-Cursor.
 *
 * `data` deliberately stays a flat TraderTradeResponse[]: ten places in
 * ProfileNew read it as an array, and changing the shape to useInfiniteQuery's
 * {pages, pageParams} would touch all of them to fix a truncation none of them
 * caused. The pages are flattened here instead, and the paging controls are
 * added beside `data` rather than replacing it. 2026-09-08.
 */
export const useTraderTrades = (traderId: string | null, status?: 'OPEN' | 'CLOSED', period: string = 'all') => {
  // The server's own page size (dashboard_endpoints.go clamps at 5,000); the
  // pages keep coming until the cursor ends (useFetchAllPages), so this is how
  // many arrive per request, not how many the trader page can show. 2026-10-07.
  const PAGE = 5000;

  const q = useInfiniteQuery({
    queryKey: ['trader-trades', traderId, status, period],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      if (!traderId) return { trades: [] as TraderTradeResponse[], total: null, nextCursor: null };
      return traderApi.getTraderTradesPage(traderId, {
        status,
        limit: PAGE,
        period,
        cursor: pageParam,
      });
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!traderId,
    staleTime: 10000,
    retry: 3,
  });

  useFetchAllPages(q);

  const flat = useMemo(
    () => (q.data?.pages ?? []).flatMap((p) => p.trades ?? []),
    [q.data],
  );

  /* The header is the truth about how many exist. Fall back to what we hold
     only when it is absent -- an older backend, or the OPEN path, which has no
     cursor and returns everything it has in one page. */
  const total = q.data?.pages?.[0]?.total ?? flat.length;

  return {
    ...q,
    data: flat,
    total,
    loadedCount: flat.length,
    hasMore: q.hasNextPage,
    fetchMore: q.fetchNextPage,
    isFetchingMore: q.isFetchingNextPage,
  };
};

/*
 * Every order the trader has, not the newest 1,000. The endpoint pages on
 * (inferred_at, id) and says how many exist (total) and where the next page
 * starts (next_cursor); the pages keep coming until it is empty. `data` stays
 * a flat array, as the trader page reads it. 2026-10-07.
 */
export const useTraderOrders = (traderId: string | null, period?: string) => {
  const PAGE = 5000;
  const q = useInfiniteQuery({
    queryKey: ['trader-orders', traderId, period],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      if (!traderId) return { orders: [] as InferredOrderResponse[], total: 0, next_cursor: '' };
      // 2026-05-16 (Nick item 5): pass period so orders feed aligns with
      // Open/Closed trades windowing instead of always all-time.
      const result = await ordersApi.getOrders({ traderId, limit: PAGE, period, cursor: pageParam });
      return { orders: result.orders || [], total: result.total ?? null, next_cursor: result.next_cursor ?? '' };
    },
    getNextPageParam: (last) => last.next_cursor || undefined,
    enabled: !!traderId,
    staleTime: 10000,
    retry: 3,
  });
  useFetchAllPages(q);
  const flat = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.orders), [q.data]);
  return { ...q, data: flat, total: q.data?.pages?.[0]?.total ?? flat.length, loadedCount: flat.length };
};

/****************
 * The traders this user has banned.
 *
 * Mirrors useFavorites exactly, including the optimistic update and the
 * rollback on failure — a ban that appears to apply and then silently does not
 * is worse than one that fails visibly, because the user walks away believing
 * they are protected.
 *
 * Banning also clears the favourite server-side (they are opposite answers to
 * one question), so the favourites query is invalidated alongside this one.
 ****************/
export const useBannedTraders = () => {
  const qc = useQueryClient();
  const { data: banned = [] } = useQuery({
    queryKey: ['banned-traders'],
    queryFn: bannedTradersApi.list,
    staleTime: 60000,
  });
  const banSet = useMemo(() => new Set(banned), [banned]);

  const toggle = useMutation({
    mutationFn: (traderUid: string) =>
      banSet.has(traderUid) ? bannedTradersApi.remove(traderUid) : bannedTradersApi.add(traderUid),
    onMutate: async (traderUid: string) => {
      await qc.cancelQueries({ queryKey: ['banned-traders'] });
      const prev = qc.getQueryData<string[]>(['banned-traders']) || [];
      const next = prev.includes(traderUid)
        ? prev.filter(id => id !== traderUid)
        : [...prev, traderUid];
      qc.setQueryData(['banned-traders'], next);
      return { prev };
    },
    onError: (_err: unknown, _uid: string, ctx: { prev: string[] } | undefined) => {
      if (ctx?.prev) qc.setQueryData(['banned-traders'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['banned-traders'] });
      // Banning clears the star server-side; without this the old star lingers
      // on screen until something else refetches.
      qc.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  return { banned: banSet, toggle: toggle.mutate };
};

export const useFavorites = () => {
  const qc = useQueryClient();
  const { data: favorites = [] } = useQuery({
    queryKey: ['favorites'],
    queryFn: favoritesApi.list,
    staleTime: 60000,
  });
  const favSet = useMemo(() => new Set(favorites), [favorites]);

  const toggle = useMutation({
    mutationFn: (traderUid: string) => {
      const removing = favSet.has(traderUid);
      track(removing ? 'trader_unfavourited' : 'trader_favourited', traderUid, undefined, { trader_uid: traderUid });
      return removing ? favoritesApi.remove(traderUid) : favoritesApi.add(traderUid);
    },
    onMutate: async (traderUid: string) => {
      await qc.cancelQueries({ queryKey: ['favorites'] });
      const prev = qc.getQueryData<string[]>(['favorites']) || [];
      const next = prev.includes(traderUid) ? prev.filter(id => id !== traderUid) : [...prev, traderUid];
      qc.setQueryData(['favorites'], next);
      return { prev };
    },
    onError: (_err: unknown, _uid: string, ctx: { prev: string[] } | undefined) => {
      if (ctx?.prev) qc.setQueryData(['favorites'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  return { favorites: favSet, toggle: toggle.mutate };
};
